import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Breadcrumbs from "@/components/Breadcrumbs";
import JsonLd from "@/components/JsonLd";
import usePageMeta from "@/hooks/use-page-meta";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Phone, MessageCircle, MapPin, Clock3 } from "lucide-react";
import * as Icons from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import { SERVICES, LOCATIONS, PHONES, WHATSAPP_URL, BRAND } from "@/constants/content";

// Route: /:serviceSlug-in-:locationSlug — e.g. /home-nursing-in-noida
export default function ServiceLocationPage() {
  const { combo } = useParams();
  const { open } = useBooking();

  const service = SERVICES.find((s) => combo?.startsWith(`${s.slug}-in-`));
  const location = service ? LOCATIONS.find((l) => combo === `${service.slug}-in-${l.slug}`) : null;

  const canonical = service && location ? `https://${BRAND.domain}/${service.slug}-in-${location.slug}` : "";
  const title = service && location ? `${service.title} in ${location.name} | KinFlex Home Healthcare` : "Not found";
  const description = service && location
    ? `${service.short} Book ${service.title.toLowerCase()} in ${location.name} — verified nurses, ${location.responseTime.toLowerCase()} response, transparent pricing.`
    : "";

  usePageMeta({ title, description, canonical, ogImage: service?.hero, ogType: "article" });

  if (!service || !location) return <Navigate to="/services" replace />;

  const Icon = Icons[service.icon] || Icons.Heart;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${service.title} in ${location.name}`,
      serviceType: service.title,
      areaServed: { "@type": "City", name: location.name },
      provider: { "@type": "MedicalBusiness", name: BRAND.name, url: `https://${BRAND.domain}` },
      description,
      url: canonical,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (service.faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <JsonLd data={jsonLd} />

      <section className="pt-28 pb-10 md:pt-36 md:pb-16 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative">
          <Breadcrumbs items={[
            { label: "Services", to: "/services" },
            { label: service.title, to: `/services/${service.slug}` },
            { label: `In ${location.name}` },
          ]} />

          <div className="mt-6 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-5">
                <MapPin className="h-3.5 w-3.5" style={{ color: '#0A84FF' }} />
                <span className="text-xs font-semibold text-slate-700">{service.title} · {location.name}</span>
              </div>
              <h1 className="font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-[52px] tracking-tight leading-[1.05]">
                {service.title} in <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">{location.name}</span>.
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">
                {service.short} Book verified caregivers in {location.name} — response within {location.responseTime.toLowerCase()}.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  <Clock3 className="h-3.5 w-3.5" style={{ color: '#10B981' }} /> Response: {location.responseTime}
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                  <MapPin className="h-3.5 w-3.5" style={{ color: '#0A84FF' }} /> {location.coverage}
                </div>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button onClick={() => open(service.title)} className="rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-7 h-12 font-semibold shadow-lg shadow-[#0A84FF]/25">
                  Book in {location.name} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <a href={`tel:${PHONES[0]}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-slate-900 px-6 h-12 font-semibold">
                  <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call {PHONES[0]}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 h-12 font-semibold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-200 premium-shadow">
              <img src={service.hero} alt={`${service.title} in ${location.name}`} className="w-full h-[380px] md:h-[460px] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="eyebrow mb-3">About this service in {location.name}</div>
              <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl tracking-tight">
                {service.title} at your doorstep in {location.name}.
              </h2>
              <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">
                KinFlex delivers {service.title.toLowerCase()} across {location.name} — {location.coverage}. Our nurses reach patients within {location.responseTime.toLowerCase()}, backed by clinical supervision and transparent pricing. {service.intro}
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">
                {service.description} Whether you're in {location.landmarks?.slice(0, 3).join(", ")} or anywhere else in {location.name}, we bring the same quality of care and rapid response.
              </p>
            </div>

            {service.benefits && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Benefits for {location.name} families</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                  {service.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#10B981' }} /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {location.landmarks && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Areas we cover in {location.name}</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {location.landmarks.map((lm) => (
                    <span key={lm} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F8FAFC] border border-slate-200 text-xs font-medium text-slate-700">
                      <MapPin className="h-3 w-3" style={{ color: '#0A84FF' }} /> {lm}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-[#0A84FF]/5 to-white p-6">
              <div className="text-xs font-bold uppercase tracking-[0.16em] mb-2" style={{ color: '#0A84FF' }}>Want more details?</div>
              <p className="text-slate-700 leading-relaxed text-sm">
                Read the full {service.title} service page for detailed features, process, benefits and FAQs.
              </p>
              <Link to={`/services/${service.slug}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A84FF]">
                See full {service.title} details <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start">
            <div className="rounded-3xl bg-slate-900 text-white p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: '#4dabff' }}>Fast Response</div>
              <h3 className="mt-3 font-display font-bold text-2xl leading-tight">
                Nurses reach {location.name} in {location.responseTime.toLowerCase()}.
              </h3>
              <div className="mt-6 space-y-2.5">
                <Button onClick={() => open(service.title)} className="w-full rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-11 font-semibold">Book Appointment</Button>
                <a href={`tel:${PHONES[0]}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 h-11 font-semibold">
                  <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call {PHONES[0]}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white h-11 font-semibold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
